import { expectedPaxTypeForSlot } from "./derivePaxTypeFromAgeOrSelection";
import { parsePaxAgeYears } from "../validateLdsPassengersConsistency";

/** Stable reason codes for LDS passenger occupancy validation. */
export const LDS_PASSENGER_ISSUE_TYPES = {
  CHILD_MAPPED_AS_ADULT: "CHILD_MAPPED_AS_ADULT",
  ADULT_MAPPED_AS_CHILD: "ADULT_MAPPED_AS_CHILD",
  MISSING_CHILD_AGE: "MISSING_CHILD_AGE",
  MISSING_DATE_OF_BIRTH: "MISSING_DATE_OF_BIRTH",
  AGE_AND_DATE_OF_BIRTH_MISMATCH: "AGE_AND_DATE_OF_BIRTH_MISMATCH",
  ROOM_ADULT_COUNT_MISMATCH: "ROOM_ADULT_COUNT_MISMATCH",
  ROOM_CHILD_COUNT_MISMATCH: "ROOM_CHILD_COUNT_MISMATCH",
  PASSENGER_ROOM_ASSIGNMENT_MISMATCH: "PASSENGER_ROOM_ASSIGNMENT_MISMATCH",
  PASSENGER_NOT_ASSIGNED_TO_ROOM: "PASSENGER_NOT_ASSIGNED_TO_ROOM",
  DUPLICATE_PASSENGER_ASSIGNMENT: "DUPLICATE_PASSENGER_ASSIGNMENT",
  UNSUPPORTED_CHILD_AGE: "UNSUPPORTED_CHILD_AGE",
  STALE_ROOM_OCCUPANCY: "STALE_ROOM_OCCUPANCY",
};

const SLOT_TO_LDS = {
  adults: "ADT",
  kids: "CHD",
  infants: "INF",
};

const PAX_TYPE_TO_LDS = {
  ADULT: "ADT",
  CHILD: "CHD",
  INFANT: "INF",
};

/**
 * @param {Record<string, unknown>|null|undefined} pax
 * @returns {string}
 */
export function formatPassengerDisplayName(pax) {
  if (!pax || typeof pax !== "object") return "";
  const first = String(pax.firstname ?? pax.firstName ?? "").trim();
  const last = String(pax.lastname ?? pax.lastName ?? "").trim();
  return [first, last].filter(Boolean).join(" ");
}

/**
 * @param {Record<string, unknown>|null|undefined} pax
 * @param {number} roomIndex
 * @param {'adults'|'kids'|'infants'} slot
 * @param {number} passengerIndex
 */
export function buildPassengerStableKey(pax, roomIndex, slot, passengerIndex) {
  const seq = pax?.["@seqNum"];
  if (seq != null && String(seq).trim() !== "") {
    return `pax-seq-${seq}`;
  }
  const roomSeq = pax?.roomSeqNum;
  if (roomSeq != null) {
    return `room-${roomSeq}-${slot}-${passengerIndex}`;
  }
  return `room-${roomIndex}-${slot}-${passengerIndex}`;
}

/**
 * @param {number} roomIndex
 * @param {Record<string, unknown>|null|undefined} room
 */
export function resolveRoomStableId(roomIndex, room) {
  const seq =
    room?.["@seqNum"] ??
    room?.room?.["@seqNum"] ??
    room?.room?.seqNum ??
    null;
  if (seq != null && String(seq).trim() !== "") {
    return `room-seq-${seq}`;
  }
  return `room-index-${roomIndex}`;
}

/**
 * @param {'adults'|'kids'|'infants'} slot
 * @param {number|null} ageYears
 * @param {ReturnType<import('../validateLdsPassengersConsistency').resolveChildAgeLimits>} childAgeLimits
 * @returns {'ADT'|'CHD'|'INF'|null}
 */
export function inferExpectedLdsTypeFromAge(slot, ageYears, childAgeLimits) {
  if (ageYears == null) return null;
  if (ageYears < childAgeLimits.infantMaxAgeYearsExclusive) return "INF";
  if (ageYears <= childAgeLimits.childMaxSlotAgeYears) return "CHD";
  return "ADT";
}

/**
 * @param {object} raw
 * @param {object} ctx
 * @returns {object}
 */
export function enrichLdsPassengerValidationIssue(raw, ctx) {
  const {
    room,
    roomIndex,
    childAgeLimits,
    referenceDate = new Date(),
  } = ctx;

  const roomId = resolveRoomStableId(roomIndex, room);
  const roomSet = String(room?.room?.roomSet ?? "").trim().toUpperCase();
  const adults = Array.isArray(room?.adults) ? room.adults : [];
  const kids = Array.isArray(room?.kids) ? room.kids : [];
  const infants = Array.isArray(room?.infants) ? room.infants : [];

  const slot = raw.slot ?? null;
  const passengerIndex =
    raw.passengerIndex != null ? Number(raw.passengerIndex) : null;
  const pax =
    slot && passengerIndex != null && room?.[slot]?.[passengerIndex]
      ? room[slot][passengerIndex]
      : null;

  const passengerId = pax
    ? buildPassengerStableKey(pax, roomIndex, slot, passengerIndex)
    : null;
  const passengerName = formatPassengerDisplayName(pax);
  const ageYears =
    raw.ageYears != null
      ? raw.ageYears
      : pax
        ? parsePaxAgeYears(pax, referenceDate)
        : null;

  const actualPassengerType = slot ? SLOT_TO_LDS[slot] : null;
  const expectedFromAge = slot
    ? inferExpectedLdsTypeFromAge(slot, ageYears, childAgeLimits)
    : null;

  let issueType = raw.issueType ?? mapLegacyCodeToIssueType(raw.code, raw);
  let field = raw.field ?? defaultFieldForIssue(issueType, slot);
  let messageHe = raw.messageHe ?? buildHebrewIssueMessage({
    issueType,
    roomIndex,
    passengerName,
    slot,
    ageYears,
    childAgeMax: childAgeLimits.childAgeMax,
    roomSet,
    raw,
    adultsCount: adults.length,
    kidsCount: kids.length,
    infantsCount: infants.length,
    parsedRoomSetTotal: raw.parsedRoomSetTotal ?? null,
    occupancyPax: raw.occupancyPax ?? adults.length + kids.length,
  });

  const expectedPassengerType =
    raw.expectedPassengerType ??
    (expectedFromAge ? PAX_TYPE_TO_LDS[expectedFromAge] : null);
  const actualTypeFromSlot =
    slot != null ? PAX_TYPE_TO_LDS[expectedPaxTypeForSlot(slot)] : actualPassengerType;

  return {
    roomIndex,
    roomId,
    passengerIndex,
    passengerId,
    passengerName: passengerName || undefined,
    slot: slot ?? undefined,
    field,
    issueType,
    expectedPassengerType: expectedPassengerType ?? undefined,
    actualPassengerType: raw.actualPassengerType ?? actualTypeFromSlot ?? undefined,
    enteredAge: ageYears ?? undefined,
    supplierMaxChildAge: childAgeLimits.childAgeMax,
    roomSet: roomSet || undefined,
    expectedAdults: raw.expectedAdults,
    actualAdults: raw.actualAdults,
    expectedChildren: raw.expectedChildren,
    actualChildren: raw.actualChildren,
    messageHe,
    legacyCode: raw.code,
  };
}

function mapLegacyCodeToIssueType(code, raw) {
  switch (code) {
    case "CHILD_IN_ADULT_SLOT":
      return LDS_PASSENGER_ISSUE_TYPES.CHILD_MAPPED_AS_ADULT;
    case "ADULT_IN_CHILD_SLOT":
      return LDS_PASSENGER_ISSUE_TYPES.ADULT_MAPPED_AS_CHILD;
    case "NON_INFANT_IN_INFANT_SLOT":
      return LDS_PASSENGER_ISSUE_TYPES.UNSUPPORTED_CHILD_AGE;
    case "MISSING_CHILD_AGE":
      return LDS_PASSENGER_ISSUE_TYPES.MISSING_CHILD_AGE;
    case "MISSING_DATE_OF_BIRTH":
      return LDS_PASSENGER_ISSUE_TYPES.MISSING_DATE_OF_BIRTH;
    case "AGE_AND_DATE_OF_BIRTH_MISMATCH":
      return LDS_PASSENGER_ISSUE_TYPES.AGE_AND_DATE_OF_BIRTH_MISMATCH;
    case "ROOM_ADULT_COUNT_MISMATCH":
      return LDS_PASSENGER_ISSUE_TYPES.ROOM_ADULT_COUNT_MISMATCH;
    case "ROOM_CHILD_COUNT_MISMATCH":
      return LDS_PASSENGER_ISSUE_TYPES.ROOM_CHILD_COUNT_MISMATCH;
    case "ROOMSET_TOTAL_PAX_MISMATCH":
      if (raw?.kidsCount > 0 && raw?.adultsCount !== raw?.parsedRoomSetTotal) {
        return LDS_PASSENGER_ISSUE_TYPES.STALE_ROOM_OCCUPANCY;
      }
      if (raw?.kidsCount === 0) {
        return LDS_PASSENGER_ISSUE_TYPES.ROOM_ADULT_COUNT_MISMATCH;
      }
      return LDS_PASSENGER_ISSUE_TYPES.STALE_ROOM_OCCUPANCY;
    default:
      return LDS_PASSENGER_ISSUE_TYPES.STALE_ROOM_OCCUPANCY;
  }
}

function defaultFieldForIssue(issueType, slot) {
  if (
    issueType === LDS_PASSENGER_ISSUE_TYPES.ROOM_ADULT_COUNT_MISMATCH ||
    issueType === LDS_PASSENGER_ISSUE_TYPES.ROOM_CHILD_COUNT_MISMATCH ||
    issueType === LDS_PASSENGER_ISSUE_TYPES.STALE_ROOM_OCCUPANCY
  ) {
    return "roomComposition";
  }
  if (issueType === LDS_PASSENGER_ISSUE_TYPES.MISSING_CHILD_AGE) {
    return "age";
  }
  return "dateOfBirth";
}

function buildHebrewIssueMessage({
  issueType,
  roomIndex,
  passengerName,
  slot,
  ageYears,
  childAgeMax,
  roomSet,
  raw,
  adultsCount,
  kidsCount,
  infantsCount,
  parsedRoomSetTotal,
  occupancyPax,
}) {
  const roomLabel = `חדר ${roomIndex + 1}`;
  const namePart = passengerName ? ` הנוסע ${passengerName}` : "";

  switch (issueType) {
    case LDS_PASSENGER_ISSUE_TYPES.CHILD_MAPPED_AS_ADULT:
      return `ב${roomLabel}${namePart} הוגדר כמבוגר, אך לפי תאריך הלידה והגבלת הגיל של החדר (עד גיל ${childAgeMax}) עליו להיות מוגדר כילד. יש לעדכן את הרכב החדר או את פרטי הנוסע.`;
    case LDS_PASSENGER_ISSUE_TYPES.ADULT_MAPPED_AS_CHILD:
      return `ב${roomLabel}${namePart} הוגדר כילד, אך לפי תאריך הלידה והגבלת הגיל של החדר (מגיל ${childAgeMax + 1}) עליו להיות מוגדר כמבוגר. יש לעדכן את הרכב החדר או את פרטי הנוסע.`;
    case LDS_PASSENGER_ISSUE_TYPES.MISSING_CHILD_AGE:
      return `ב${roomLabel}${namePart} חסר גיל או תאריך לידה לילד. יש למלא את השדה לפני המשך ההזמנה.`;
    case LDS_PASSENGER_ISSUE_TYPES.MISSING_DATE_OF_BIRTH:
      return `ב${roomLabel}${namePart} חסר תאריך לידה. יש למלא את השדה לפני המשך ההזמנה.`;
    case LDS_PASSENGER_ISSUE_TYPES.AGE_AND_DATE_OF_BIRTH_MISMATCH:
      return `ב${roomLabel}${namePart} הגיל שהוזן אינו תואם לתאריך הלידה. יש לתקן את אחד מהשדות.`;
    case LDS_PASSENGER_ISSUE_TYPES.UNSUPPORTED_CHILD_AGE:
      return `ב${roomLabel}${namePart} הוגדר כתינוק, אך הגיל שחושב מתאריך הלידה (${ageYears}) אינו מתאים לתינוק (מתחת לגיל 2).`;
    case LDS_PASSENGER_ISSUE_TYPES.ROOM_ADULT_COUNT_MISMATCH:
      return `${roomLabel} הוזמן עבור ${parsedRoomSetTotal ?? raw.parsedRoomSetTotal} מבוגרים (${roomSet || "לפי הרכב החדר"}), אך בפרטי הנוסעים שובצו ${adultsCount} מבוגרים. יש לעדכן את הרכב או את פרטי הנוסעים.`;
    case LDS_PASSENGER_ISSUE_TYPES.ROOM_CHILD_COUNT_MISMATCH:
      return `${roomLabel} — אי־התאמה במספר הילדים בין הרכב החדר לפרטי הנוסעים.`;
    case LDS_PASSENGER_ISSUE_TYPES.STALE_ROOM_OCCUPANCY:
      return `${roomLabel} הוזמן עבור ${parsedRoomSetTotal ?? raw.parsedRoomSetTotal} נוסעים בתשלום (${roomSet || "לפי הרכב"}), אך בפרטי הנוסעים שובצו ${occupancyPax} מבוגרים/ילדים${infantsCount ? ` ועוד ${infantsCount} תינוקות` : ""}. יש לעדכן את הרכב החדר או את פרטי הנוסעים לפני המשך ההזמנה.`;
    default:
      return `ב${roomLabel} נמצאה אי־התאמה בפרטי הנוסעים. יש לבדוק את הרכב והגילאים.`;
  }
}

/**
 * @param {object[]} rawErrors
 * @param {unknown} roomsAndPaxInfo
 * @param {object} options
 */
export function enrichLdsPassengerValidationIssues(
  rawErrors,
  roomsAndPaxInfo,
  options = {}
) {
  const rooms = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo : [];
  const childAgeLimits = options.childAgeLimits;
  const referenceDate = options.referenceDate ?? new Date();

  return (rawErrors || []).map((raw) =>
    enrichLdsPassengerValidationIssue(raw, {
      room: rooms[raw.roomIndex],
      roomIndex: raw.roomIndex,
      childAgeLimits,
      referenceDate,
    })
  );
}
